import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { 
  Zap, ArrowRight, Play, CheckCircle, Users, BarChart3, 
  Workflow, Clock, Shield, Globe, Star, ChevronRight 
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function LandingPage() {
  const navigate = useNavigate();
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Handle plan selection and Stripe checkout
  const handlePlanSelection = async (plan) => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login/signup
      navigate('/login', { state: { selectedPlan: plan.name } });
      return;
    }

    // Free plan - activate directly
    if (plan.price === 0) {
      try {
        setCheckoutLoading(true);
        await axios.post(
          `${API}/stripe/subscription/activate-free`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        navigate('/dashboard?subscription=activated');
      } catch (error) {
        console.error('Failed to activate free plan:', error);
        alert('Failed to activate free plan. Please try again.');
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }

    // Paid plan - redirect to Stripe checkout
    try {
      setCheckoutLoading(true);
      const response = await axios.post(
        `${API}/stripe/create-checkout-session`,
        {
          priceId: plan.stripePriceId,
          planName: plan.name,
          successUrl: `${window.location.origin}/dashboard?checkout=success`,
          cancelUrl: `${window.location.origin}/pricing?checkout=cancelled`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      alert('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  useEffect(() => {
    // Fetch pricing from API
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/subscription/plans`);
        if (response.data && response.data.plans) {
          // Filter only active plans and sort by tier
          const activePlans = response.data.plans
            .filter(plan => plan.isActive !== false)
            .sort((a, b) => a.tier - b.tier);
          setPricingPlans(activePlans);
        }
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Set default fallback pricing if API fails
        setPricingPlans([
          {
            displayName: "Quantum Starter",
            price: 0,
            description: "Perfect for individuals",
            features: ["100 executions/month", "3 workflows", "Basic integrations"],
            tier: 1,
            isPopular: false
          },
          {
            displayName: "Quantum Professional",
            price: 79,
            description: "For power users",
            features: ["10,000 executions/month", "25 workflows", "Advanced integrations"],
            tier: 2,
            isPopular: false
          },
          {
            displayName: "Quantum Team",
            price: 249,
            description: "For growing teams",
            features: ["100,000 executions/month", "Unlimited workflows", "24/7 support"],
            tier: 3,
            isPopular: true
          },
          {
            displayName: "Quantum Enterprise",
            price: 999,
            description: "For large organizations",
            features: ["Unlimited executions", "White-label", "Dedicated support"],
            tier: 4,
            isPopular: false
          }
        ]);
      } finally {
        setLoadingPricing(false);
      }
    };
    fetchPricing();
  }, []);

  const features = [
    { icon: Workflow, title: "Visual Workflow Builder", desc: "Drag-and-drop interface to create complex automations without code" },
    { icon: Zap, title: "Instant Webhooks", desc: "Connect any service with generic webhooks that trigger your workflows" },
    { icon: Clock, title: "Smart Scheduling", desc: "Run workflows on schedules or trigger them based on events" },
    { icon: Shield, title: "Enterprise Security", desc: "Role-based access control and encrypted data at rest" },
    { icon: BarChart3, title: "Real-time Analytics", desc: "Monitor execution history and track performance metrics" },
    { icon: Users, title: "Team Collaboration", desc: "Invite team members and manage permissions easily" },
  ];

  const templates = [
    { name: "Lead Alert Notification", category: "Sales" },
    { name: "Invoice Processing", category: "Finance" },
    { name: "Customer Onboarding", category: "Marketing" },
    { name: "Form to CRM", category: "Sales" },
  ];

  const testimonials = [
    { name: "Sarah Chen", role: "Operations Manager", company: "TechStart Inc", text: "FlowSync reduced our manual data entry by 80%. The workflow builder is incredibly intuitive." },
    { name: "Michael Torres", role: "Real Estate Agent", company: "Premier Properties", text: "I automate all my lead follow-ups now. Saved me 15+ hours per week." },
    { name: "Emily Watson", role: "Accountant", company: "Watson & Associates", text: "Invoice processing that used to take days now happens automatically. Game changer." },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-light border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl gradient-active flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-heading font-semibold text-foreground">FlowSync</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">Features</a>
            <a href="#templates" className="text-muted-foreground hover:text-foreground transition-colors">Templates</a>
            <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login-btn">
              Sign In
            </Button>
            <Button onClick={() => navigate('/signup')} className="rounded-full bg-primary hover:bg-primary/90" data-testid="nav-get-started-btn">
              Get Started <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="gradient-hero min-h-screen flex items-center pt-20">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-teal-400 text-sm mb-6">
              <Star className="w-4 h-4" /> Trusted by 2,000+ businesses
            </div>
            <h1 className="text-5xl md:text-7xl font-heading font-semibold text-white tracking-tight leading-tight mb-6">
              Automate Your <span className="text-teal-400">Business</span> Workflows
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-lg">
              Build powerful automations without code. Connect your tools, automate repetitive tasks, and focus on what matters most.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                onClick={() => navigate('/signup')}
                className="rounded-full bg-teal-400 hover:bg-teal-500 text-navy-500 px-8 py-6 text-lg font-medium shadow-lg hover:shadow-teal-400/25"
                data-testid="hero-get-started-btn"
              >
                Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
              >
                <Play className="w-5 h-5 mr-2" /> Watch Demo
              </Button>
            </div>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative hidden md:block"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-teal-400/20 to-transparent rounded-3xl blur-3xl" />
            <div className="relative glass-heavy rounded-3xl p-6 border border-white/10">
              <img 
                src="https://images.unsplash.com/photo-1760112982249-2ac83f3a27aa?w=800" 
                alt="Workflow automation" 
                className="rounded-2xl w-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-semibold text-foreground tracking-tight mb-4">
              Everything you need to automate
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Powerful features designed for small businesses, accountants, and consultants.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group glass-card rounded-2xl p-8 hover:border-primary/30"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Section */}
      <section id="templates" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-heading font-semibold text-foreground tracking-tight mb-4">
                Start with pre-built templates
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Choose from 12+ ready-to-use workflow templates. Customize them to fit your needs or build from scratch.
              </p>
              <div className="space-y-4">
                {templates.map((template, i) => (
                  <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{template.name}</p>
                      <p className="text-sm text-muted-foreground">{template.category}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground ml-auto" />
                  </div>
                ))}
              </div>
              <Button 
                className="mt-8 rounded-full" 
                variant="outline"
                onClick={() => navigate('/login')}
              >
                View All Templates <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl" />
              <img 
                src="https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?w=800" 
                alt="Team collaboration" 
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-semibold text-foreground tracking-tight mb-4">
              Loved by businesses
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="glass-card rounded-2xl p-8"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-foreground mb-6">"{testimonial.text}"</p>
                <div>
                  <p className="font-medium text-foreground">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}, {testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-heading font-semibold text-foreground tracking-tight mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-muted-foreground">Start free. Scale as you grow.</p>
          </div>
          {loadingPricing ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="glass-card rounded-3xl p-8 animate-pulse">
                  <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-muted rounded w-full mb-6"></div>
                  <div className="h-16 bg-muted rounded mb-6"></div>
                  <div className="space-y-3 mb-8">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="h-4 bg-muted rounded"></div>
                    ))}
                  </div>
                  <div className="h-10 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {pricingPlans.map((plan, index) => (
                <div
                  key={plan.name || index}
                  className={`glass-card rounded-3xl p-8 relative transition-all hover:scale-105 ${
                    plan.isPopular ? 'border-2 border-primary shadow-xl' : ''
                  }`}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-semibold rounded-full shadow-lg">
                      ⭐ MOST POPULAR
                    </div>
                  )}
                  <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
                    {plan.displayName}
                  </h3>
                  <p className="text-muted-foreground mb-6 text-sm">
                    {plan.description}
                  </p>
                  <div className="mb-6">
                    <span className="text-5xl font-heading font-bold text-foreground">
                      ${plan.price}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                  </div>
                  <ul className="space-y-3 mb-8 min-h-[200px]">
                    {plan.features && plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-foreground text-sm">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full rounded-full ${
                      plan.isPopular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                        : ''
                    }`}
                    variant={plan.isPopular ? 'default' : 'outline'}
                    onClick={() => handlePlanSelection(plan)}
                    data-testid={`${plan.name}-plan-btn`}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading ? 'Loading...' : (plan.price === 0 ? 'Start Free' : 'Get Started')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 gradient-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-semibold text-white tracking-tight mb-6">
            Ready to automate your business?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Join thousands of businesses saving time and money with FlowSync.
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/signup')}
            className="rounded-full bg-teal-400 hover:bg-teal-500 text-navy-500 px-8 py-6 text-lg font-medium"
            data-testid="cta-get-started-btn"
          >
            Start Your Free Trial <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-500 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg gradient-active flex items-center justify-center">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-heading font-semibold text-white">FlowSync</span>
            </div>
            <p className="text-slate-400 text-sm">
              © 2025 FlowSync. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
